/**
 * IdentityRequiredDialog — shown when the active org tenant can't auto-resolve
 * the caller's identity because they belong to it under MORE than one email
 * (e.g. owner via a@, member via b@). The server returns 401 `org_email_required`
 * with the candidate identities (email + role) and tenant name; rather than
 * surface that as a raw drive error, we let the user pick which identity to act
 * as — rendered "<tenant> · <email> (<role>)" to match the share-link picker.
 *
 * This typically fires on a DEEP LINK straight into an org Drive
 * (`/t/<org>/folder`, e.g. "Open in my Drive" from a share) — the normal
 * tenant-switcher path sets the identity before navigating, so it never asks.
 *
 * Rendered at App level (outside the workspace ThemedArea), so it applies the
 * theme class explicitly — same rule the sibling App dialogs follow.
 */
import { useState } from 'react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'

interface Identity {
  email: string
  role: string
}

interface Props {
  /** Display name of the ambiguous tenant, when known. */
  tenantName: string | null
  /** Candidate identities (the user's memberships in this tenant) with roles. */
  identities: Identity[]
  /** Commit the chosen identity (email). */
  onPick: (email: string) => void
  /** Bail out to the personal Drive instead of choosing. */
  onUsePersonal: () => void
}

export function IdentityRequiredDialog({ tenantName, identities, onPick, onUsePersonal }: Props) {
  // The role as a label (Owner, Drive Admin, Member…), not its raw id.
  const { t } = useTranslation('common')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [selected, setSelected] = useState(identities[0]?.email ?? '')

  const where = tenantName ? <span className="font-medium themed-text">{tenantName}</span> : 'this organization'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className={`${themeClass} themed-bg-secondary themed-text border themed-border rounded-lg shadow-2xl w-full max-w-md p-6 relative`}>
        <h2 className="text-base font-semibold themed-text mb-2">Choose your identity</h2>
        <p className="text-sm themed-text-secondary leading-relaxed mb-4">
          You belong to {where} under more than one email. Pick which one to act
          as — it affects your role and what you can do here.
        </p>

        <div className="flex flex-col gap-2">
          {identities.map(({ email, role }) => (
            <label
              key={email}
              className={`flex items-start gap-2.5 px-3 py-2.5 rounded-md border cursor-pointer transition-colors ${
                selected === email ? 'border-blue-500 themed-bg-tertiary' : 'themed-border hover:themed-bg-tertiary'
              }`}
            >
              <input
                type="radio"
                name="identity-email"
                value={email}
                checked={selected === email}
                onChange={() => setSelected(email)}
                className="accent-blue-500 mt-0.5"
              />
              <span className="min-w-0">
                <span className="block text-sm themed-text truncate">
                  {tenantName ? `${tenantName} · ${email}` : email}
                </span>
                <span className="block text-xs themed-text-muted">{t(`roles.${role}`, { defaultValue: role })}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onUsePersonal}
            className="themed-btn-secondary px-4 py-2 rounded-md text-sm font-medium"
          >
            Go to my Drive
          </button>
          <button
            type="button"
            onClick={() => selected && onPick(selected)}
            disabled={!selected}
            className="themed-btn-primary px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  )
}
